import React, { useEffect, useMemo, useState } from "react";
import {
  Alert,
  Avatar,
  Box,
  Button,
  Checkbox,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControlLabel,
  IconButton,
  Paper,
  Stack,
  TextField,
  ToggleButton,
  ToggleButtonGroup,
  Typography,
} from "@mui/material";
import { getAvatar } from "../../utils/avatar";
import ExpandLessIcon from "@mui/icons-material/ExpandLess";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import SendIcon from "@mui/icons-material/Send";
import HistoryIcon from "@mui/icons-material/History";
// Template chips use small colourful emoji (decorative — the label
// text carries the meaning for screen readers). The one MUI icon
// still needed is the error-retry chip below.
import ReportGmailerrorredOutlinedIcon from "@mui/icons-material/ReportGmailerrorredOutlined";
import {
  useGetClassSessionMessagesQuery,
  useSendClassSessionMessageMutation,
  useGetAttendanceMessageTemplatesQuery,
} from "../../Api/attoDeskApi";

// 2026-08-25: added "staff" (assigned occurrence teacher) and "all"
// (parents + staff + admin, deduplicated server-side). "both" is
// kept only for backward-compat with any in-flight prefill / history
// payloads emitted by earlier versions — server accepts it as
// parents+admin. New UI never uses "both".
export type MessageRecipients = "parents" | "admin" | "staff" | "all" | "both";

export interface SendMessagePanelStudent {
  id: number;
  name: string;
  /** Parent name for template personalisation (optional) */
  parentName?: string | null;
}

export interface SendMessagePanelContext {
  className: string;
  grade?: string | null;
  staffName?: string | null;
  dateLabel: string;
  startTime?: string | null;
  endTime?: string | null;
}

export interface SendMessagePanelProps {
  occurrenceId: number;
  classId?: number;
  students: SendMessagePanelStudent[];
  context: SendMessagePanelContext;
  /** Optional pre-fill (used by the attendance page absent-prompt flow). */
  prefill?: {
    recipients?: MessageRecipients;
    studentIds?: number[];
    template?: TemplateKey;
    open?: boolean;
    /** Increment to re-apply prefill programmatically. */
    version?: number;
  };
  onSent?: (parentCount: number, adminCount: number) => void;
  /** Show history section (default true). */
  showHistory?: boolean;
  /** Start collapsed (default true). */
  startCollapsed?: boolean;
  title?: string;
}

// Prefill callers still use short legacy keys ("absent"/"late"/…).
// Attendance-context tenant templates use `attendance_*` slugs; map
// the two here so an absent-prompt "Send Now" still resolves to the
// current template row without callers needing to know the new key.
export type TemplateKey =
  | "absent" | "behaviour" | "homework" | "progress" | "late" | "custom";

const LEGACY_KEY_MAP: Record<TemplateKey, string> = {
  absent:    "attendance_absent",
  behaviour: "attendance_behaviour",
  homework:  "attendance_homework",
  progress:  "attendance_progress",
  late:      "attendance_late",
  custom:    "",
};

// Icon-by-default-key. Emoji stay stable across template renames
// (admin can change `name` from Settings) because they're bound to
// `default_key`, not the visible label.
const TEMPLATE_ICONS: Record<string, string> = {
  attendance_absent:    "📋",
  attendance_late:      "⏰",
  attendance_behaviour: "⚠️",
  attendance_homework:  "📚",
  attendance_progress:  "🎉",
};
const CUSTOM_ICON = "📝";
const FALLBACK_ICON = "💬";
const CUSTOM_KEY = "__custom__";

/**
 * Per-template colour identity — a controlled semantic palette:
 *   coral | gold | lavender | sky | mint | steel
 *
 * Unselected chips already hint at their own colour (very pale tint)
 * so the row scans as a "controlled rainbow" instead of six greys.
 * Selected chips deepen the tint + strengthen the border rather than
 * flipping to a dark fill — no dark green anywhere in the component.
 */
interface ChipTheme {
  /** Idle */
  bg: string; border: string; text: string; icon: string;
  /** Selected */
  selBg: string; selBorder: string; selText: string; selIcon: string;
  selShadow: string;
  /** Focus-visible outline (~35% alpha of the family colour) */
  focusRing: string;
}
const THEME_BY_KEY: Record<string, ChipTheme> = {
  // Absent Today — coral / soft rose (semantic: absence / attention)
  attendance_absent: {
    bg: "#FFF5F5",   border: "#FAD4D8", text: "#B65B65", icon: "#D96A73",
    selBg: "#FFE6E8", selBorder: "#E88A93", selText: "#8C3F48", selIcon: "#C94756",
    selShadow: "0 3px 10px rgba(140,63,72,0.10)",
    focusRing: "rgba(217,106,115,0.35)",
  },
  // Late Today — warm gold (semantic: late / warning)
  attendance_late: {
    bg: "#FFFDF2",   border: "#F6E7A4", text: "#9A7A16", icon: "#C69B14",
    selBg: "#FFF8D8", selBorder: "#F2D05C", selText: "#8A6A08", selIcon: "#E5B800",
    selShadow: "0 3px 10px rgba(138,106,8,0.10)",
    focusRing: "rgba(229,184,0,0.35)",
  },
  // Behaviour Note — soft lavender (semantic: note / observational)
  attendance_behaviour: {
    bg: "#F8F5FF",   border: "#DDD3F5", text: "#6D5C9A", icon: "#8676B8",
    selBg: "#ECE4FF", selBorder: "#B7A3EA", selText: "#4A3A80", selIcon: "#6E58B4",
    selShadow: "0 3px 10px rgba(74,58,128,0.10)",
    focusRing: "rgba(110,88,180,0.35)",
  },
  // Homework Due — sky blue (semantic: assignment / informational)
  attendance_homework: {
    bg: "#F1FAFF",   border: "#C9EAF8", text: "#397A9F", icon: "#3E96C7",
    selBg: "#DEF1FB", selBorder: "#7EC3E1", selText: "#1F5E82", selIcon: "#1E7DB2",
    selShadow: "0 3px 10px rgba(31,94,130,0.10)",
    focusRing: "rgba(30,125,178,0.35)",
  },
  // Great Progress — mint / aqua (semantic: success / progress).
  // Mint stays soft — no forest green anywhere.
  attendance_progress: {
    bg: "#F2FBF8",   border: "#C9E8DD", text: "#3F806C", icon: "#3F9F82",
    selBg: "#DEF4EC", selBorder: "#7CC7B0", selText: "#22624C", selIcon: "#1F8F70",
    selShadow: "0 3px 10px rgba(34,98,76,0.10)",
    focusRing: "rgba(31,143,112,0.35)",
  },
};
// Custom — steel blue / slate (matches the app's primary identity).
const CUSTOM_THEME: ChipTheme = {
  bg: "#F5F8FB",   border: "#D9E3EC", text: "#58728C", icon: "#5F86AC",
  selBg: "#E4ECF5", selBorder: "#7EA3C3", selText: "#274F70", selIcon: "#31577F",
  selShadow: "0 3px 10px rgba(39,79,112,0.10)",
  focusRing: "rgba(49,87,127,0.35)",
};

const chipThemeFor = (key: string): ChipTheme =>
  key === CUSTOM_KEY ? CUSTOM_THEME : (THEME_BY_KEY[key] ?? CUSTOM_THEME);

/** Compose the sx bag for a template chip — idle + selected + hover. */
const chipSx = (theme: ChipTheme, selected: boolean) => ({
  height: 32,
  minHeight: 32,
  borderRadius: "8px",
  fontSize: 12.5,
  fontWeight: selected ? 700 : 500,
  color: selected ? theme.selText : theme.text,
  bgcolor: selected ? theme.selBg : theme.bg,
  border: `1px solid ${selected ? theme.selBorder : theme.border}`,
  boxShadow: selected ? theme.selShadow : "none",
  transform: selected ? "translateY(-1px)" : "none",
  transition: "all 160ms ease",
  "& .MuiChip-icon": {
    // Emoji glyphs — decorative. Size + spacing per spec so the
    // icon vertically centres with the label and stays compact.
    marginLeft: "8px",
    marginRight: "-2px",
    fontSize: 13,
    lineHeight: 1,
    display: "inline-flex",
    alignItems: "center",
  },
  "& .MuiChip-label": { paddingLeft: "5px" },
  "&:hover": {
    bgcolor: selected ? theme.selBg : theme.bg,
    borderColor: selected ? theme.selBorder : theme.selBorder,
    transform: "translateY(-1px)",
    boxShadow: selected ? theme.selShadow : "0 2px 6px rgba(16,32,54,0.06)",
  },
  "&:focus-visible": {
    outline: "none",
    boxShadow: `0 0 0 2px ${theme.focusRing}`,
  },
});

interface FetchedTemplate {
  id: number;
  key: string;                // default_key from backend
  name: string;
  subject_template: string;
  body_template: string;
  sort_order: number;
}

const MAX_STUDENTS = 6;

const SendMessagePanel: React.FC<SendMessagePanelProps> = ({
  occurrenceId,
  classId,
  students,
  context,
  prefill,
  onSent,
  showHistory = true,
  startCollapsed = true,
  title = "Send Message to Parents / Admin",
}) => {
  const [open, setOpen] = useState(!startCollapsed);
  const [recipients, setRecipients] = useState<MessageRecipients>("parents");
  const [studentIds, setStudentIds] = useState<number[]>([]);
  const [subject, setSubject] = useState("");
  const [body, setBody] = useState("");
  const [templateId, setTemplateId] = useState<number | null>(null);
  const [templateKey, setTemplateKey] = useState<string | null>(null);
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [toast, setToast] = useState<{ msg: string; severity: "success" | "error" | "info" } | null>(null);
  const [historyOpen, setHistoryOpen] = useState(false);

  const [sendMessage, sendState] = useSendClassSessionMessageMutation();
  const tplQuery = useGetAttendanceMessageTemplatesQuery(undefined, { skip: !open });
  const templates: FetchedTemplate[] =
    (tplQuery.data?.data as any)?.templates ?? [];
  const tplLoading = tplQuery.isLoading || tplQuery.isFetching;

  const historyQuery = useGetClassSessionMessagesQuery(
    { occurrenceId, classId, limit: 5 },
    { skip: !showHistory || !occurrenceId }
  );
  const history = (historyQuery.data?.data as any[]) || [];

  const capStudents = useMemo(() => students.slice(0, MAX_STUDENTS), [students]);

  // Subject-field focus ring inherits the selected template's colour
  // so the composer visually connects the picked chip → subject field.
  const selectedTheme: ChipTheme | null = useMemo(() => {
    if (templateId === null) return null;
    const row = templates.find((t) => t.id === templateId);
    return row ? chipThemeFor(row.key) : null;
  }, [templateId, templates]);

  // Client-side placeholder preview — server re-renders authoritatively
  // per-recipient at send time, this is JUST for the composer's preview.
  const previewFill = (s: string, forStudentIds?: number[]): string => {
    const ids = forStudentIds ?? studentIds;
    const firstStudent = capStudents.find((x) => ids.includes(x.id)) || capStudents[0] || null;
    const values: Record<string, string> = {
      student_name: firstStudent?.name || "[Student Name]",
      studentName:  firstStudent?.name || "[Student Name]",
      parentName:   firstStudent?.parentName || "Parent/Guardian",
      class_name:   context.className,
      staff_name:   context.staffName || "",
      staffName:    context.staffName || "",
      session_date: context.dateLabel,
      start_time:   context.startTime || "",
      end_time:     context.endTime || "",
      location:     "",
      recipient_label: firstStudent?.parentName || "",
      attendance_status: "",
      late_minutes: "",
    };
    let out = s;
    Object.entries(values).forEach(([k, v]) => {
      out = out.split(`{{${k}}}`).join(v).split(`{${k}}`).join(v);
    });
    return out;
  };

  const applyTemplateRow = (row: FetchedTemplate, forStudentIds?: number[]) => {
    setTemplateId(row.id);
    setTemplateKey(row.key);
    setSubject(previewFill(row.subject_template, forStudentIds));
    setBody(previewFill(row.body_template, forStudentIds));
  };

  const applyLegacyKey = (key: TemplateKey, forStudentIds?: number[]) => {
    if (key === "custom") {
      setTemplateId(null); setTemplateKey(null);
      setSubject(""); setBody("");
      return;
    }
    const canonical = LEGACY_KEY_MAP[key];
    const row = templates.find((t) => t.key === canonical);
    if (row) applyTemplateRow(row, forStudentIds);
  };

  // React to external pre-fill (e.g. absent-prompt "Send Now" on
  // attendance page). Prefill may fire before the templates query
  // resolves — in that case the useEffect below re-applies once the
  // templates land.
  useEffect(() => {
    if (!prefill) return;
    if (prefill.open) setOpen(true);
    if (prefill.recipients) setRecipients(prefill.recipients);
    if (prefill.studentIds) {
      const capped = prefill.studentIds.slice(0, MAX_STUDENTS);
      setStudentIds(capped);
      if (prefill.template) {
        applyLegacyKey(prefill.template, capped);
      }
    } else if (prefill.template) {
      applyLegacyKey(prefill.template);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [prefill?.version]);

  // If prefill arrived before the template list was ready, apply now.
  useEffect(() => {
    if (!prefill?.template || templateId != null || templates.length === 0) return;
    applyLegacyKey(prefill.template, prefill.studentIds);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [templates.length]);

  const toggleStudent = (id: number, checked: boolean) => {
    setStudentIds((ids) => {
      if (checked) {
        if (ids.includes(id)) return ids;
        if (ids.length >= MAX_STUDENTS) {
          setToast({
            msg: `You can select up to ${MAX_STUDENTS} students per message.`,
            severity: "info",
          });
          return ids;
        }
        return [...ids, id];
      }
      return ids.filter((x) => x !== id);
    });
  };

  const selectAll = () => {
    setStudentIds(capStudents.map((s) => s.id));
  };
  const deselectAll = () => setStudentIds([]);

  const validate = (): string | null => {
    if ((recipients === "parents" || recipients === "both" || recipients === "all") && studentIds.length === 0) {
      return "Select at least one student.";
    }
    if (!subject.trim()) return "Subject is required.";
    if (subject.length > 191) return "Subject exceeds 191 characters.";
    if (!body.trim()) return "Message is required.";
    if (body.length > 5000) return "Message exceeds 5000 characters.";
    return null;
  };

  const trySend = () => {
    const err = validate();
    if (err) {
      setToast({ msg: err, severity: "error" });
      return;
    }
    if (
      (recipients === "parents" || recipients === "both" || recipients === "all") &&
      studentIds.length > 1
    ) {
      setConfirmOpen(true);
      return;
    }
    doSend();
  };

  const doSend = async () => {
    setConfirmOpen(false);
    try {
      const res: any = await sendMessage({
        occurrenceId,
        recipients,
        student_ids: studentIds,
        subject: subject.trim(),
        message: body.trim(),
        template: templateKey ?? undefined,
        template_id: templateId ?? undefined,
      }).unwrap();
      const p = res?.data?.parent_emails_sent ?? 0;
      const a = res?.data?.admin_emails_sent ?? 0;
      const s = res?.data?.staff_emails_sent ?? 0;
      const parts: string[] = [];
      if (p) parts.push(`${p} parent${p === 1 ? "" : "s"}`);
      if (s) parts.push(`${s} staff`);
      if (a) parts.push(`${a} admin`);
      setToast({
        msg: parts.length > 0
          ? `Message sent to ${parts.join(", ")}.`
          : `Message sent, but no recipients could be resolved.`,
        severity: parts.length > 0 ? "success" : "info",
      });
      setSubject("");
      setBody("");
      setStudentIds([]);
      setTemplateId(null);
      setTemplateKey(null);
      onSent?.(p, a);
      historyQuery.refetch();
    } catch (e: any) {
      setToast({
        msg: e?.data?.message || "Failed to send message.",
        severity: "error",
      });
    }
  };

  const parentCount = studentIds.length;

  return (
    <>
      <Paper
        variant="outlined"
        sx={{
          borderRadius: 2,
          overflow: "hidden",
          borderTop: "3px solid #31577F",
          boxShadow: "0 1px 3px rgba(0,0,0,0.04)",
        }}
      >
        <Stack
          direction="row"
          alignItems="center"
          justifyContent="space-between"
          sx={{ px: 2, py: 1.25, bgcolor: "grey.50", cursor: "pointer" }}
          onClick={() => setOpen((v) => !v)}
        >
          <Typography sx={{ fontWeight: 600, color: "#31577F" }}>
            📨 {title}
          </Typography>
          <IconButton size="small">
            {open ? <ExpandLessIcon /> : <ExpandMoreIcon />}
          </IconButton>
        </Stack>

        {open && (
          <Box sx={{ p: 2 }}>
            <Stack spacing={2}>
              <Box>
                <Typography variant="caption" color="text.secondary">
                  To:
                </Typography>
                <Box sx={{ mt: 0.5 }}>
                  <ToggleButtonGroup
                    exclusive
                    size="small"
                    value={recipients === "both" ? "all" : recipients}
                    onChange={(_, v) => v && setRecipients(v)}
                    sx={{
                      "& .MuiToggleButton-root": {
                        textTransform: "none",
                        fontWeight: 600,
                        color: "#687C90",
                        bgcolor: "#FFFFFF",
                        borderColor: "#DDE6EE",
                        transition: "all 160ms ease",
                        "&:hover": { bgcolor: "#F5F8FB", borderColor: "#C6D4E1" },
                      },
                      "& .MuiToggleButton-root.Mui-selected": {
                        bgcolor: "#EAF2F8",
                        color: "#274F70",
                        borderColor: "#7EA3C3",
                        "&:hover": { bgcolor: "#DDEBF4" },
                      },
                    }}
                  >
                    <ToggleButton value="parents">Parents / Guardians</ToggleButton>
                    <ToggleButton value="staff">Class Staff</ToggleButton>
                    <ToggleButton value="admin">Admin / Office</ToggleButton>
                    <ToggleButton value="all">All</ToggleButton>
                  </ToggleButtonGroup>
                  {/* Class-Staff recipient identity card — read-only
                      (comes from the occurrence DTO's staff assignment,
                      no picker). Server side rejects any forged
                      staff_id, so this UI is purely a preview. */}
                  {(recipients === "staff" || recipients === "all") && (
                    <Box sx={{
                      mt: 1.5, p: 1.5, borderRadius: "10px",
                      bgcolor: "#EAF2F8", border: "1px solid #DDE6EE",
                      display: "flex", alignItems: "center", gap: 1.25,
                    }}>
                      {context.staffName ? (
                        <>
                          <Avatar sx={{ width: 34, height: 34, bgcolor: "#4E78A6", color: "#fff", fontSize: 13, fontWeight: 800 }}>
                            {context.staffName.split(/\s+/).filter(Boolean).slice(0, 2).map((n) => n[0]?.toUpperCase()).join("") || "S"}
                          </Avatar>
                          <Box sx={{ minWidth: 0 }}>
                            <Typography sx={{ fontSize: 13, fontWeight: 800, color: "#17324D" }}>{context.staffName}</Typography>
                            <Typography sx={{ fontSize: 11, color: "#708397" }}>Assigned to this session</Typography>
                          </Box>
                        </>
                      ) : (
                        <Typography sx={{ fontSize: 12.5, color: "#B75F68", fontWeight: 700 }}>
                          No staff member is assigned to this session.
                        </Typography>
                      )}
                    </Box>
                  )}
                </Box>
              </Box>

              {(recipients === "parents" || recipients === "both" || recipients === "all") && (
                <Box>
                  <Stack direction="row" alignItems="center" spacing={1}>
                    <Typography variant="caption" color="text.secondary">
                      Students (max {MAX_STUDENTS})
                    </Typography>
                    <Button size="small" onClick={selectAll} sx={{ textTransform: "none" }}>
                      Select all
                    </Button>
                    <Button size="small" onClick={deselectAll} sx={{ textTransform: "none" }}>
                      Clear
                    </Button>
                  </Stack>
                  <Box sx={{ display: "flex", flexWrap: "wrap", gap: 0.5, mt: 0.5 }}>
                    {capStudents.map((s) => (
                      <FormControlLabel
                        key={s.id}
                        control={
                          <Checkbox
                            size="small"
                            checked={studentIds.includes(s.id)}
                            onChange={(e) => toggleStudent(s.id, e.target.checked)}
                          />
                        }
                        label={
                          <Stack direction="row" spacing={0.75} alignItems="center">
                            <Avatar
                              src={getAvatar(s.name, null, 32)}
                              alt={s.name}
                              sx={{ width: 24, height: 24 }}
                            />
                            <Typography variant="body2">{s.name}</Typography>
                          </Stack>
                        }
                        sx={{ mr: 2 }}
                      />
                    ))}
                    {students.length > MAX_STUDENTS && (
                      <Typography variant="caption" color="text.secondary" sx={{ ml: 1, alignSelf: "center" }}>
                        Showing first {MAX_STUDENTS} of {students.length}.
                      </Typography>
                    )}
                  </Box>
                </Box>
              )}

              <Box>
                <Typography
                  sx={{
                    fontSize: 11,
                    fontWeight: 800,
                    textTransform: "uppercase",
                    letterSpacing: "0.06em",
                    color: "#61758A",
                  }}
                >
                  Quick Templates
                </Typography>
                <Box sx={{ display: "flex", flexWrap: "wrap", gap: 0.75, mt: 0.75 }}>
                  {tplLoading && templates.length === 0 && (
                    <>
                      {[0, 1, 2, 3].map((i) => (
                        <Chip
                          key={i}
                          label="Loading…"
                          size="small"
                          sx={{
                            height: 32, borderRadius: "8px", fontSize: 12,
                            bgcolor: "#F2F6FA", color: "#8B9AAB", border: "1px solid #E1E8EF",
                            opacity: 0.7,
                          }}
                        />
                      ))}
                    </>
                  )}
                  {!tplLoading && tplQuery.isError && (
                    <Chip
                      label="Failed to load templates — retry"
                      size="small"
                      icon={<ReportGmailerrorredOutlinedIcon />}
                      clickable
                      onClick={() => tplQuery.refetch()}
                      sx={chipSx(THEME_BY_KEY.attendance_absent, true)}
                    />
                  )}
                  {templates.map((t) => {
                    const emoji = TEMPLATE_ICONS[t.key] || FALLBACK_ICON;
                    const theme = chipThemeFor(t.key);
                    const selected = templateId === t.id;
                    return (
                      <Chip
                        key={t.id}
                        icon={<span aria-hidden="true">{emoji}</span>}
                        label={t.name}
                        clickable
                        onClick={() => applyTemplateRow(t)}
                        size="small"
                        sx={chipSx(theme, selected)}
                      />
                    );
                  })}
                  <Chip
                    icon={<span aria-hidden="true">{CUSTOM_ICON}</span>}
                    label="Custom"
                    clickable
                    onClick={() => { setTemplateId(null); setTemplateKey(null); setSubject(""); setBody(""); }}
                    size="small"
                    sx={chipSx(CUSTOM_THEME, templateId === null && !!(subject || body))}
                  />
                </Box>
              </Box>

              <TextField
                label="Subject"
                value={subject}
                onChange={(e) => setSubject(e.target.value.slice(0, 191))}
                fullWidth
                size="small"
                helperText={`${subject.length} / 191`}
                sx={{
                  "& .MuiOutlinedInput-root": {
                    bgcolor: "#FFFFFF",
                    color: "#17324D",
                    "& fieldset": { borderColor: "#DCE5ED" },
                    "&:hover fieldset": { borderColor: "#B7C6D6" },
                    // When a template is picked, the Subject field's
                    // focus ring picks up the template's own colour —
                    // subtle, focus-only, never permanent.
                    "&.Mui-focused fieldset": {
                      borderColor: selectedTheme?.selBorder ?? "#5F86AC",
                      borderWidth: 2,
                    },
                  },
                }}
              />
              <TextField
                label="Message"
                value={body}
                onChange={(e) => setBody(e.target.value.slice(0, 5000))}
                fullWidth
                multiline
                minRows={4}
                helperText={`${body.length} / 5000`}
                sx={{
                  "& .MuiOutlinedInput-root": {
                    bgcolor: "#FFFFFF",
                    color: "#17324D",
                    "& fieldset": { borderColor: "#DCE5ED" },
                    "&:hover fieldset": { borderColor: "#B7C6D6" },
                    "&.Mui-focused fieldset": { borderColor: "#5F86AC", borderWidth: 2 },
                  },
                }}
              />

              <Stack direction="row" spacing={2} justifyContent="flex-end">
                <Button
                  onClick={() => setOpen(false)}
                  sx={{
                    textTransform: "none",
                    color: "#31577F",
                    fontWeight: 600,
                    "&:hover": { bgcolor: "#F2F6FA" },
                  }}
                >
                  Cancel
                </Button>
                <Button
                  variant="contained"
                  endIcon={<SendIcon />}
                  onClick={trySend}
                  disabled={sendState.isLoading}
                  sx={{
                    textTransform: "none",
                    fontWeight: 700,
                    bgcolor: "#31577F",
                    boxShadow: "0 3px 10px rgba(49,87,127,0.20)",
                    "&:hover": { bgcolor: "#274A6B" },
                  }}
                >
                  Send Message
                </Button>
              </Stack>
            </Stack>
          </Box>
        )}
      </Paper>

      {/* Message History */}
      {showHistory && (
        <Paper variant="outlined" sx={{ borderRadius: 2, overflow: "hidden", mt: 2 }}>
          <Stack
            direction="row"
            alignItems="center"
            justifyContent="space-between"
            sx={{ px: 2, py: 1.25, bgcolor: "grey.50", cursor: "pointer" }}
            onClick={() => setHistoryOpen((v) => !v)}
          >
            <Stack direction="row" spacing={1} alignItems="center">
              <HistoryIcon fontSize="small" sx={{ color: "#31577F" }} />
              <Typography sx={{ fontWeight: 600 }}>
                Message History {history.length > 0 ? `(${history.length})` : ""}
              </Typography>
            </Stack>
            <IconButton size="small">
              {historyOpen ? <ExpandLessIcon /> : <ExpandMoreIcon />}
            </IconButton>
          </Stack>
          {historyOpen && (
            <Box sx={{ p: 2 }}>
              {history.length === 0 && (
                <Typography variant="body2" color="text.secondary">
                  No messages sent yet.
                </Typography>
              )}
              {history.map((m: any) => (
                <Box
                  key={m.id}
                  sx={{
                    py: 1.25,
                    borderTop: "1px solid #f0f0f0",
                    "&:first-of-type": { borderTop: 0 },
                  }}
                >
                  <Typography variant="caption" color="text.secondary">
                    {m.sent_at
                      ? new Date(m.sent_at).toLocaleString(undefined, {
                          month: "short",
                          day: "numeric",
                          year: "numeric",
                          hour: "numeric",
                          minute: "2-digit",
                        })
                      : ""}
                  </Typography>
                  <Typography variant="body2" sx={{ fontWeight: 500 }}>
                    {m.subject}
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    To:{" "}
                    {m.recipients === "both"
                      ? "Parents & Admin"
                      : m.recipients === "admin"
                      ? "Admin"
                      : "Parents"}
                    {m.template ? ` · "${m.template}"` : ""}
                  </Typography>
                  {m.student_names?.length > 0 && (
                    <Stack direction="row" spacing={0.75} sx={{ mt: 0.5, flexWrap: "wrap" }}>
                      {m.student_names.slice(0, 6).map((n: string, i: number) => (
                        <Chip
                          key={i}
                          size="small"
                          avatar={
                            <Avatar
                              src={getAvatar(n, null, 24)}
                              alt={n}
                            />
                          }
                          label={n}
                          sx={{ height: 22 }}
                        />
                      ))}
                      {m.student_names.length > 6 && (
                        <Typography variant="caption" color="text.secondary" sx={{ alignSelf: "center" }}>
                          +{m.student_names.length - 6}
                        </Typography>
                      )}
                    </Stack>
                  )}
                </Box>
              ))}
            </Box>
          )}
        </Paper>
      )}

      {/* Multi-parent confirmation dialog */}
      <Dialog open={confirmOpen} onClose={() => setConfirmOpen(false)}>
        <DialogTitle>Confirm send</DialogTitle>
        <DialogContent>
          <Typography>
            You are about to send emails to <strong>{parentCount}</strong>{" "}
            parent{parentCount === 1 ? "" : "s"}
            {recipients === "both" ? " and the admin" : ""}. Continue?
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setConfirmOpen(false)}>Cancel</Button>
          <Button variant="contained" onClick={doSend} disabled={sendState.isLoading}>
            Send
          </Button>
        </DialogActions>
      </Dialog>

      {toast && (
        <Alert
          severity={toast.severity}
          onClose={() => setToast(null)}
          sx={{
            position: "fixed",
            bottom: 20,
            right: 20,
            zIndex: 1500,
            boxShadow: 3,
          }}
        >
          {toast.msg}
        </Alert>
      )}
    </>
  );
};

export default SendMessagePanel;
